import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Card,
  Input,
  Select,
  DatePicker,
} from "@velocity-ehs/component-library";
import { useEffect } from "react";
import FormSkeleton from "../../../sharedComponents/FormSkeleton";
import { UserForm } from "../../UserManagement/User";
import { PersonCustomerRelationship } from "../../Persons/PersonForm";
import UsersService from "@velocity-ehs/user-utils-module";
import { hasAnyAddressValue } from "../MyProfileFormHelpers";
import { genderSelectOptions } from "../../Persons/Person";
import { emailRegExp } from "../../../supportFunctions/ValidationHelpers";

type PersonalDetailsProps = {
  formMethods: UseFormReturn<UserForm, unknown>;
  isLoading: boolean;
  isReadOnly: boolean;
};

type CONDITIONAL_ADDRESS_FIELDS =
  | "streetAddress"
  | "zipPostal"
  | "city"
  | "stateProv"
  | "country";

const ADDRESS_FIELDS: CONDITIONAL_ADDRESS_FIELDS[] = [
  "streetAddress",
  "zipPostal",
  "city",
  "stateProv",
  "country",
];

const PersonalDetails = ({
  formMethods,
  isLoading,
  isReadOnly,
}: PersonalDetailsProps): React.ReactElement => {
  const { t } = useTranslation();

  const canEditSensitiveData = UsersService.permissions.canEditSensitiveData;

  const {
    control,
    formState: { errors },
    trigger,
    watch,
    clearErrors,
  } = formMethods;

  const hasAddress = hasAnyAddressValue(watch);

  useEffect(() => {
    if (!hasAddress) {
      clearErrors(ADDRESS_FIELDS);
      trigger(ADDRESS_FIELDS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAddress]);

  const form = (
    <>
      <Input
        readOnly={isReadOnly}
        control={control}
        name="firstName"
        label={t("universal_First_Name")}
        rules={{
          required: t("customer_Form_Admin_User_First_Name_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        errorMessage={errors && errors.firstName?.message}
      />
      <Input
        readOnly={isReadOnly}
        control={control}
        name="lastName"
        label={t("universal_Last_Name")}
        rules={{
          required: t("customer_Form_Admin_User_Last_Name_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        errorMessage={errors && errors.lastName?.message}
      />
      <Select
        label={t("universal_Gender")}
        readOnly={!canEditSensitiveData}
        control={control}
        name="gender"
        options={genderSelectOptions()}
        errorMessage={errors && errors.gender?.message}
      />
      <DatePicker
        readOnly={!canEditSensitiveData}
        control={control}
        name="birthDate"
        label={t("person_Birth_Date")}
        errorMessage={errors ? errors.birthDate?.message : undefined}
      />
      <Input
        readOnly={!canEditSensitiveData}
        control={control}
        name="streetAddress"
        label={t("universal_Street_Address")}
        errorMessage={errors ? errors.streetAddress?.message : undefined}
        rules={{
          required: hasAddress
            ? t("person_Form_Street_Address_Required")
            : false,
        }}
      />
      <Input
        readOnly={!canEditSensitiveData}
        control={control}
        name="address2"
        label={`${t("universal_Street_Address")} 2`}
        errorMessage={errors ? errors.address2?.message : undefined}
      />
      <Input
        readOnly={!canEditSensitiveData}
        control={control}
        name="city"
        label={t("universal_City")}
        errorMessage={errors ? errors.city?.message : undefined}
        rules={{
          required: hasAddress ? t("person_Form_City_Required") : false,
        }}
      />
      <Input
        readOnly={!canEditSensitiveData}
        control={control}
        name="zipPostal"
        label={t("universal_Zip_Code")}
        errorMessage={errors ? errors.zipPostal?.message : undefined}
        rules={{
          required: hasAddress ? t("person_Form_Zip_Code_Required") : false,
        }}
      />
      <Input
        readOnly={!canEditSensitiveData}
        control={control}
        name="stateProv"
        label={t("universal_State_Province")}
        errorMessage={errors ? errors.stateProv?.message : undefined}
        rules={{
          required: hasAddress ? t("person_Form_State_Prov_Required") : false,
        }}
      />
      <Input
        readOnly={!canEditSensitiveData}
        control={control}
        name="country"
        label={t("universal_country")}
        errorMessage={errors ? errors.country?.message : undefined}
        rules={{
          required: hasAddress ? t("person_Form_Country_Required") : false,
        }}
      />
      <Input
        readOnly={isReadOnly}
        control={control}
        name="email"
        label={t("universal_Email")}
        rules={{
          required: t("customer_Form_Admin_User_Email_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
          pattern: {
            value: emailRegExp,
            message: t("message_invalid_email_address"),
          },
        }}
        errorMessage={errors && errors.email?.message}
      />
      <Input
        readOnly={isReadOnly}
        control={control}
        name="displayedLoginName"
        label={t("universal_Username")}
        rules={{
          required: t("customer_Form_Admin_User_Username_Required"),
          maxLength: {
            value: 64,
            message: t("customer_Form_Domain_Max_Length_validation"),
          },
          pattern: {
            value: /^\s*[^+\\;,]+\s*$/i,
            message: t("message_invalid_username"),
          },
        }}
        errorMessage={errors && errors.displayedLoginName?.message}
      />
      <Input
        readOnly={isReadOnly}
        control={control}
        name="phone"
        label={t("universal_Phone")}
        rules={{
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        errorMessage={errors && errors.phone?.message}
      />
      <Select
        label={t("person_Customer_Relationship")}
        readOnly={!UsersService.permissions.canEditPersons}
        control={control}
        name="person.relationshipToCustomer"
        options={PersonCustomerRelationship()}
        required
        errorMessage={errors && errors.person?.relationshipToCustomer?.message}
      />
    </>
  );

  return (
    <Card
      title={t("profile_Personal_Details_Text")}
      style={{ paddingBottom: "2px" }}
    >
      {isLoading ? <FormSkeleton rows={5} /> : form}
    </Card>
  );
};

export { PersonalDetails };
